<template>
  <div class="app-shell">
    <el-header v-if="!hideGlobalHeader" class="app-header" height="56px">
      <div class="header-left">
        <TransitionGroup v-if="hasPageTabs" ref="pageTabsRef" name="tab-move" tag="div" class="page-tabs">
          <div
            v-for="tab in pageTabs"
            :key="tab.key"
            :class="['page-tab', { active: tab.key === activePageTabKey, dragging: draggingTabKey === tab.key }]"
            :data-key="tab.key"
            draggable="true"
            @click="handleTabClick(tab)"
            @dragstart="handleTabDragStart(tab, $event)"
            @dragover.prevent="handleTabDragOver(tab, $event)"
            @drop.prevent="handleTabDragEnd"
            @dragend="handleTabDragEnd"
          >
            <span
              :class="[
                'page-tab-dot',
                {
                  'page-tab-dot--filled': tabDotState(tab) === 'filled',
                  'page-tab-dot--running': tabDotState(tab) === 'running',
                },
              ]"
              aria-hidden="true"
            />
            <span class="page-tab-label" :title="tab.label">{{ tab.label }}</span>
            <button v-if="tab.closable" class="page-tab-close" type="button" @click.stop="handleTabClose(tab)">
              <el-icon><Close /></el-icon>
            </button>
          </div>
        </TransitionGroup>
      </div>
      <div class="header-right">
        <div class="header-tools">
          <button
            v-if="canOpenOrgAdmin"
            class="header-icon-btn"
            type="button"
            title="组织架构设置"
            aria-label="组织架构设置"
            @click="goOrgAdmin"
          >
            <el-icon :size="16"><Setting /></el-icon>
          </button>
          <button
            class="header-icon-btn header-text-btn"
            type="button"
            title="产品使用手册"
            aria-label="帮助"
            @click="goHelp"
          >
            帮助
          </button>
          <button
            v-if="isSmokeTestUser"
            ref="smokeTestBtnRef"
            class="header-smoke-btn"
            type="button"
            data-smoke-test-btn="1"
            :title="smokeTestButtonLabel"
            :aria-label="smokeTestButtonLabel"
            :class="{
              'is-running': smokeTestStatus === 'running',
              'is-paused': smokeTestStatus === 'paused',
            }"
            @click="onSmokeTestClick"
          >
            {{ smokeTestButtonLabel }}
          </button>
          <HeaderNotificationBell :unread-count="unreadCount" @refresh-unread="() => refreshUnreadCount(true)" />
        </div>
        <SmokeTestProgressPanel
          v-if="isSmokeTestUser"
          :status="smokeTestStatus"
          :current-step="smokeTestCurrentStep"
          :logs="smokeTestLogs"
          :suspect-bugs="smokeTestBugs"
          :anchor-rect="smokeTestAnchorRect"
          :bubble-visible="smokeTestBubbleVisible"
          :report-visible="smokeTestReportVisible"
          @collapse="collapseSmokeBubble"
          @open-report="openSmokeReport"
          @update:report-visible="setSmokeReportVisible"
        />
        <el-dropdown
          trigger="click"
          :teleported="true"
          popper-class="header-user-dropdown-popper"
          @command="handleUserCommand"
        >
          <span class="user-trigger user-info">
            <el-avatar
              :size="32"
              :src="headerAvatarUrl"
              class="header-avatar"
            >
              {{ displayName.charAt(0) }}
            </el-avatar>
            <span class="user-name">{{ displayName }}</span>
            <el-icon class="el-icon--right"><ArrowDown /></el-icon>
          </span>
          <template #dropdown>
            <el-dropdown-menu>
              <div class="user-roles-header">
                <span class="roles-label">角色：</span>
                <template v-if="headerRolesForDisplay.length">
                  <el-tag
                    v-for="item in headerRolesForDisplay"
                    :key="item.code"
                    size="small"
                    class="header-role-tag"
                  >
                    {{ item.label }}
                  </el-tag>
                </template>
                <el-tag v-else size="small" type="info" class="header-role-tag">未配置</el-tag>
              </div>
              <el-dropdown-item command="profile">
                <el-icon><User /></el-icon>
                个人信息
              </el-dropdown-item>
              <el-dropdown-item command="logout" divided>
                <el-icon><SwitchButton /></el-icon>
                退出登录
              </el-dropdown-item>
            </el-dropdown-menu>
          </template>
        </el-dropdown>
      </div>
    </el-header>
    <PrimarySidebar />
    <div class="app-shell-body">
      <main
        :class="[
          'app-main',
          {
            'no-header': hideGlobalHeader,
            'has-ai-panel': useAiRightReserve,
            'app-main--project-create': route.name === 'ProjectCreate',
            'app-main--project-dashboard': route.name === 'ProjectDashboard',
            'app-main--ai-smart-review': route.name === 'AiSmartReview',
            'app-main--dashboard': route.name === 'Dashboard',
            'app-main--org-admin': route.name === 'OrgAdmin',
            'app-main--smart-document':
              route.name === 'SmartDocumentWorkbench' || route.name === 'SmartDocumentWorkbenchHome',
            'app-main--ai-tools': route.name === 'AiTools',
            'app-main--ai-calculator':
              route.name === 'AiCalculator' || route.name === 'AiCalculatorDetail',
            'app-main--knowledge-space': route.name === 'KnowledgeSpace',
            'app-main--ai-requirement-ledger': route.name === 'AiRequirementLedger',
            'app-main--resources': route.name === 'Resources',
            'app-main--ai-3d-viewer': route.name === 'Ai3DViewer',
          },
        ]"
        :style="useAiRightReserve ? { '--ai-panel-reserve': `${aiPanelReserve}px` } : undefined"
      >
        <router-view v-slot="{ Component: routeComponent, route: activeRoute }">
          <template v-if="routeComponent">
            <!-- 不按组件 name 过滤：script setup 页面默认无 name，include 会导致切换页签时销毁重建并触发 loading -->
            <keep-alive :max="12">
              <component
                v-if="isActiveRouteKeepAlive(activeRoute)"
                :is="routeComponent"
                :key="routeKeepAliveCacheKey(activeRoute)"
              />
            </keep-alive>
            <component
              v-if="!isActiveRouteKeepAlive(activeRoute)"
              :is="routeComponent"
              :key="activeRoute.fullPath"
            />
          </template>
        </router-view>
        <ExpandedAiComposer v-if="shellStore.dashboardExpandedAiComposer" key="expanded-ai-composer" />
        <AiAssistantPanel
          v-if="dockAiPanel"
          ref="aiPanelRef"
          @collapse-change="aiPanelCollapsed = $event"
          @width-change="aiPanelWidth = $event"
        />
      </main>
    </div>
    <Teleport to="body">
      <Transition name="project-autofill-intro-fade">
        <div
          v-if="shellStore.showProjectAutofillIntro"
          class="project-autofill-intro-overlay"
          role="status"
          aria-live="polite"
        >
          <div class="project-autofill-intro-inner">
            <div class="project-autofill-intro-ring" aria-hidden="true" />
            <p class="project-autofill-intro-title">开始自动填充项目信息</p>
            <p class="project-autofill-intro-sub">正在打开创建项目页…</p>
          </div>
        </div>
      </Transition>
    </Teleport>
    <AiPageContentQuoteFloater
      :visible="aiQuoteFloater.visible"
      :top="aiQuoteFloater.top"
      :left="aiQuoteFloater.left"
      @quote="quoteToAi"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, watch, provide } from 'vue';
import { useRouter, useRoute, type RouteLocationRaw } from 'vue-router';
import { useAuthStore } from '@/stores/useAuthStore';
import { usePermissionStore } from '@/stores/usePermissionStore';
import { useAppShellStore, type ShellPageTab } from '@/stores/useAppShellStore';
import { ArrowDown, User, SwitchButton, Setting, Close } from '@element-plus/icons-vue';
import AiAssistantPanel from '@/components/AiAssistantPanel.vue';
import ExpandedAiComposer from '@/components/ExpandedAiComposer.vue';
import AiPageContentQuoteFloater from '@/components/AiPageContentQuoteFloater.vue';
import PrimarySidebar from '@/components/PrimarySidebar.vue';
import { useAiPageContentQuote } from '@/composables/useAiPageContentQuote';
import HeaderNotificationBell from '@/components/HeaderNotificationBell.vue';
import { getMyUnreadCount } from '@/api/notificationApi';
import { useProjectCreateDraftStore } from '@/stores/useProjectCreateDraftStore';
import {
  collectKeepAliveRouteNames,
  isRouteKeepAlive,
  routeKeepAliveCacheKey,
} from '@/utils/routeKeepAlive';
import {
  formatProjectTabLabel,
  projectDashboardTabKey,
  updateProjectDashboardTabLabel,
} from '@/utils/projectPageTab';
import { isProjectNameRequestCurrent, type ProjectNameRequestContext } from '@/utils/aiContextGuards';
import { pageTabRouteKey } from '@/utils/pageTabRouteKey';
import { PROJECT_WBS_TAB_ID } from '@/utils/projectDetailTabState';
import { useSmokeTestRunner } from '@/composables/useSmokeTestRunner';
import SmokeTestProgressPanel from '@/components/SmokeTestProgressPanel.vue';
import { OrgAdminMatrix } from '@/constants/matrixLeaves';
import { usePermission } from '@/composables/usePermission';
import { sanitizeAvatarUrl } from '@/utils/sanitizeAvatarUrl';
import { confirmProjectCreateDraftLeave } from '@/utils/projectCreateTabLeave';

const router = useRouter();
const route = useRoute();
const authStore = useAuthStore();
const permissionStore = usePermissionStore();
const { checkMatrixPermission } = usePermission();
const shellStore = useAppShellStore();
// 必须先恢复完整列表，再由下方路由监听激活当前 URL；权限仍由路由守卫校验。
let preserveEmptyDashboardTabs = shellStore.restorePageTabs((to) => router.resolve(to).matched.length > 0)
  && !shellStore.hasPageTabs();
const projectCreateDraftStore = useProjectCreateDraftStore();

const aiPanelRef = ref<InstanceType<typeof AiAssistantPanel> | null>(null);
provide('aipdAiPanelRef', aiPanelRef);

const smokeTestBtnRef = ref<HTMLButtonElement | null>(null);
const smokeTestAnchorRect = ref<{
  top: number;
  left: number;
  bottom: number;
  right: number;
  width: number;
} | null>(null);

const {
  isSmokeTestUser,
  status: smokeTestStatus,
  buttonLabel: smokeTestButtonLabel,
  currentStepLabel: smokeTestCurrentStep,
  suspectBugs: smokeTestBugs,
  opLogs: smokeTestLogs,
  bubbleVisible: smokeTestBubbleVisible,
  reportVisible: smokeTestReportVisible,
  onButtonClick: onSmokeTestClick,
  collapseBubble: collapseSmokeBubble,
  openReport: openSmokeReport,
  setReportVisible: setSmokeReportVisible,
} = useSmokeTestRunner(() => aiPanelRef.value);

function syncSmokeTestAnchorRect() {
  const el = smokeTestBtnRef.value;
  if (!el) {
    smokeTestAnchorRect.value = null;
    return;
  }
  const r = el.getBoundingClientRect();
  smokeTestAnchorRect.value = {
    top: r.top,
    left: r.left,
    bottom: r.bottom,
    right: r.right,
    width: r.width,
  };
}

watch(
  [smokeTestBubbleVisible, isSmokeTestUser, smokeTestStatus],
  async ([visible]) => {
    if (!visible) return;
    await nextTick();
    syncSmokeTestAnchorRect();
  },
);

onMounted(() => {
  window.addEventListener('resize', syncSmokeTestAnchorRect);
});
onUnmounted(() => {
  window.removeEventListener('resize', syncSmokeTestAnchorRect);
});
const { floater: aiQuoteFloater, quoteToAi } = useAiPageContentQuote(aiPanelRef);

function normalizeRoleCode(c: string) {
  return String(c || '')
    .trim()
    .replace(/^ROLE_/i, '')
    .toUpperCase();
}

function roleCodeToShortLabel(code: string) {
  const c = normalizeRoleCode(code);
  const map: Record<string, string> = {
    SUPER_ADMIN: '超级管理员',
    ADMIN: '子管理员',
    USER: '用户',
    SE: '系统工程师',
    PM: '项目经理',
    PO: '项目经理',
    DEV: '开发工程师',
    QA: '质量工程师',
    ARCH: '系统工程师',
    READONLY: '只读',
    PROTO_SIM: '试制/仿真',
    COST: '成本核价专员',
    PE: '流程工程师',
  };
  return map[c] || code;
}

const headerRolesForDisplay = computed(() => {
  const codes = new Set<string>();
  for (const r of permissionStore.roles || []) {
    if (r) codes.add(String(r));
  }
  for (const r of authStore.roles || []) {
    if (r) codes.add(String(r));
  }
  return Array.from(codes).map((code) => ({
    code,
    label: roleCodeToShortLabel(code),
  }));
});

const currentUser = computed(() => authStore.userInfo);
const displayName = computed(() => authStore.userInfo?.realName || authStore.userInfo?.username || '用户');
const headerAvatarUrl = computed(() =>
  sanitizeAvatarUrl(currentUser.value?.avatar || currentUser.value?.avatarUrl),
);
const hideGlobalHeader = computed(() => false);
const showAiPanel = computed(() => route.name !== 'Login' && route.name !== 'WeComCallback');
/** 创建项目页 / 智能文档等与全局一致：右侧常驻通用 AI 抽屉 */
const dockAiPanel = computed(() => showAiPanel.value);
const suppressRightAiPanel = computed(() => shellStore.dashboardExpandedAiComposer);
const aiPanelCollapsed = ref(false);
const aiPanelWidth = ref(320);
const AI_PANEL_RIGHT_OFFSET = 10;
const AI_PANEL_GUTTER = 44;
const AI_PANEL_GUTTER_COMPACT = 8;
const SMART_DOC_ROUTE_NAMES = new Set(['SmartDocumentWorkbench', 'SmartDocumentWorkbenchHome']);
const aiPanelReserve = computed(() => {
  const name = String(route.name || '');
  const gutter =
    name === 'Dashboard' ||
    name === 'ProjectDashboard' ||
    name === 'ProjectCreate' ||
    name === 'AiTools' ||
    name === 'AiCalculator' ||
    name === 'AiCalculatorDetail' ||
    name === 'AiRequirementLedger' ||
    name === 'Resources' ||
    name === 'KnowledgeSpace' ||
    SMART_DOC_ROUTE_NAMES.has(name)
      ? AI_PANEL_GUTTER_COMPACT
      : AI_PANEL_GUTTER;
  return aiPanelWidth.value + AI_PANEL_RIGHT_OFFSET + gutter;
});
const sidebarRailWidth = computed(() =>
  shellStore.sidebarCollapsed ? shellStore.PRIMARY_RAIL_COLLAPSED_WIDTH : shellStore.PRIMARY_RAIL_WIDTH,
);
const useAiRightReserve = computed(
  () => dockAiPanel.value && !aiPanelCollapsed.value && !suppressRightAiPanel.value,
);

const projectName = ref<string>('');
const projectSemanticVersion = ref<string>('');
provide('aipdShellProjectName', projectName);
const breadcrumbComponentName = ref<string>('');
const breadcrumbFormulaName = ref<string>('');
const breadcrumbOfflinePreviewStem = ref<string>('');
const unreadCount = ref<number>(0);
let lastUnreadFetchAt = 0;

function routeToKey(to: RouteLocationRaw) {
  return pageTabRouteKey(to);
}

function getRouteTabLabel(targetRoute: typeof route) {
  if (targetRoute.name === 'KnowledgeSpace') return '知识空间';
  if (targetRoute.name === 'Dashboard') return '项目总览';
  if (targetRoute.name === 'SmartDocumentWorkbench' || targetRoute.name === 'SmartDocumentWorkbenchHome') {
    return '智能文档';
  }
  if (targetRoute.name === 'ProjectCreate') {
    const dk = String(targetRoute.query?.draft ?? '');
    return dk ? projectCreateDraftStore.tabTitleForDraft(dk) : '新项目项目创建';
  }
  if (targetRoute.name === 'AiCalculatorDetail') {
    return breadcrumbFormulaName.value || (targetRoute.meta?.title as string | undefined) || '计算器';
  }
  if (targetRoute.name === 'AiSmartReview') {
    return 'AI智审';
  }
  if (targetRoute.name === 'AiRequirementDetail') {
    const tabKey = `AiRequirementDetail:${String(targetRoute.params.id ?? '')}`;
    const existing = shellStore.pageTabs.find((t) => t.key === tabKey);
    const label = existing?.label?.trim();
    if (label && label !== '需求详情') return label;
    return '加载中…';
  }
  if (targetRoute.name === 'ProjectWbsSnapshot') {
    return 'WBS 快照';
  }
  const title = targetRoute.meta?.title as string | undefined;
  return title || (typeof targetRoute.name === 'string' ? targetRoute.name : '页面');
}

function getTabFromRoute(targetRoute: typeof route): ShellPageTab {
  const query = { ...targetRoute.query };
  // AI 需求详情与项目无关，页签还原时丢弃误带的 projectId
  if (targetRoute.name === 'AiRequirementDetail' && 'projectId' in query) {
    delete query.projectId;
  }
  const to: RouteLocationRaw = {
    name: targetRoute.name as string,
    params: { ...targetRoute.params },
    query,
  };
  const key = routeToKey(to);
  const existing = shellStore.pageTabs.find((t) => t.key === key);
  return {
    key,
    label: existing?.label ?? getRouteTabLabel(targetRoute),
    to,
    closable: true,
    type: 'route',
  };
}

function getProjectTabFromRoute(targetRoute: typeof route): ShellPageTab {
  const pid = String(targetRoute.params?.id ?? '');
  const tabKey = projectDashboardTabKey(pid);
  const existing = shellStore.pageTabs.find((t) => t.key === tabKey);
  const to = {
    name: 'ProjectDashboard',
    params: { ...targetRoute.params },
  };
  if (existing?.label && existing.label !== '项目详情' && existing.label !== '加载中…') {
    return { ...existing, to };
  }
  const routePid = String(targetRoute.params?.id ?? '');
  const nameMatchesRoute =
    projectName.value &&
    routePid &&
    String(route.params?.id ?? '') === routePid;
  const label = nameMatchesRoute
    ? (formatProjectTabLabel(projectName.value, projectSemanticVersion.value) || '加载中…')
    : (existing?.label || '加载中…');
  return {
    key: tabKey,
    label,
    to,
    closable: true,
    type: 'route',
  };
}

const pageTabs = computed(() => shellStore.pageTabs);
const activePageTabKey = computed(() => shellStore.activePageTabKey);

const keepAliveIncludeNames = computed(() =>
  collectKeepAliveRouteNames(
    pageTabs.value
      .map((tab) => {
        const to = tab.to as { name?: string } | string;
        return typeof to === 'object' && to != null ? String(to.name ?? '') : '';
      })
      .filter(Boolean),
  ),
);

function isActiveRouteKeepAlive(activeRoute: typeof route) {
  return isRouteKeepAlive(activeRoute, keepAliveIncludeNames.value);
}
const hasPageTabs = computed(() => shellStore.hasPageTabs());
const pageTabsRef = ref<HTMLElement | null>(null);
const draggingTabKey = ref('');
/** 防止 dragover 对同一落点重复 reorder 造成抖动 */
let lastLiveReorderKey = '';

function getPageTabsHostEl(): HTMLElement | null {
  const r = pageTabsRef.value as unknown;
  if (!r) return null;
  if (r instanceof HTMLElement) return r;
  const el = (r as { $el?: unknown }).$el;
  return el instanceof HTMLElement ? el : null;
}

function scrollActiveTabIntoView() {
  const container = getPageTabsHostEl();
  if (!container) return;
  const activeEl = container.querySelector<HTMLElement>('.page-tab.active');
  if (!activeEl) return;
  const containerRect = container.getBoundingClientRect();
  const activeRect = activeEl.getBoundingClientRect();
  if (activeRect.left < containerRect.left) {
    container.scrollTo({ left: container.scrollLeft + (activeRect.left - containerRect.left) - 16, behavior: 'smooth' });
  } else if (activeRect.right > containerRect.right) {
    container.scrollTo({ left: container.scrollLeft + (activeRect.right - containerRect.right) + 16, behavior: 'smooth' });
  }
}

watch(activePageTabKey, async () => {
  await nextTick();
  scrollActiveTabIntoView();
});

provide('setBreadcrumbComponentName', (name: string) => {
  breadcrumbComponentName.value = name || '';
});

provide('setBreadcrumbFormulaName', (name: string) => {
  breadcrumbFormulaName.value = name || '';
  if (route.name === 'AiCalculatorDetail' && name) {
    shellStore.updatePageTab(
      routeToKey({
        name: route.name as string,
        params: { ...route.params },
        query: { ...route.query },
      }),
      { label: name },
    );
  }
});

provide('setBreadcrumbOfflinePreviewStem', (stem: string) => {
  breadcrumbOfflinePreviewStem.value = stem || '';
});

let projectNameLoadGeneration = 0;

function resolveRouteProjectIdForName(): number {
  const raw = route.params?.id;
  const parsed = Array.isArray(raw) ? Number(raw[0]) : Number(raw);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

async function loadProjectNameIfNeeded() {
  const routesWhereParamsIdIsProjectId = new Set([
    'ProjectDashboard',
    'ProjectRequirements',
    'ProjectDocumentViewer',
  ]);
  const requestContext: ProjectNameRequestContext = {
    generation: ++projectNameLoadGeneration,
    routeName: String(route.name ?? ''),
    projectId: resolveRouteProjectIdForName(),
  };
  const isCurrentRequest = () => isProjectNameRequestCurrent(requestContext, {
    generation: projectNameLoadGeneration,
    routeName: String(route.name ?? ''),
    projectId: resolveRouteProjectIdForName(),
  });
  const clearProjectName = () => {
    projectName.value = '';
    projectSemanticVersion.value = '';
  };

  if (!requestContext.routeName || !routesWhereParamsIdIsProjectId.has(requestContext.routeName)) {
    if (isCurrentRequest()) clearProjectName();
    return;
  }
  const id = requestContext.projectId;
  if (!id) {
    if (isCurrentRequest()) clearProjectName();
    return;
  }
  // 切换页签后先清掉旧名称，等待当前项目响应；旧请求即使晚返回也不能再写入。
  if (isCurrentRequest()) clearProjectName();
  try {
    const mod = await import('@/api/projectApi');
    const p = await mod.getProject(id);
    if (!isCurrentRequest()) return;
    projectName.value = p?.name || '';
    projectSemanticVersion.value = p?.semanticVersion || '';
    updateProjectDashboardTabLabel(id, p?.name, p?.semanticVersion);
  } catch {
    if (!isCurrentRequest()) return;
    clearProjectName();
  }
}

watch(
  () => route.fullPath,
  () => {
    if (route.name !== 'ResourceDetail') breadcrumbComponentName.value = '';
    if (route.name !== 'Ai3DViewerOfflinePreview' && route.name !== 'Ai3DViewerSharePreview') breadcrumbOfflinePreviewStem.value = '';
    if (route.name !== 'AiCalculatorDetail') breadcrumbFormulaName.value = '';
    if (route.name && typeof route.name === 'string') {
      if (route.name === 'ProjectDocumentViewer') {
        const pid = String(route.params.id ?? '');
        let objectKey = String(route.params.objectKey ?? '');
        try {
          objectKey = decodeURIComponent(objectKey);
        } catch {
          /* keep */
        }
        const fileName = String(route.query.fileName ?? '').trim();
        void router.replace({
          name: 'ProjectDashboard',
          params: { id: pid },
          query: {
            innerTab: 'doc',
            objectKey,
            ...(fileName ? { fileName } : {}),
          },
        });
        return;
      }
      if (route.name === 'ProjectWbsSnapshot') {
        const pid = String(route.params.id ?? '');
        void router.replace({
          name: 'ProjectDashboard',
          params: { id: pid },
          query: {
            innerTab: PROJECT_WBS_TAB_ID,
            ...(String(route.query.generate || '') === '1' ? { generate: '1' } : {}),
          },
        });
        return;
      }
      if (route.name === 'Dashboard' && preserveEmptyDashboardTabs && !shellStore.hasPageTabs()) {
        // 关闭全部标签后的空会话，刷新时不重新创建 Dashboard 标签。
      } else if (route.name === 'ProjectDashboard') {
        shellStore.openPageTab(getProjectTabFromRoute(route));
      } else {
        shellStore.openPageTab(getTabFromRoute(route));
      }
      preserveEmptyDashboardTabs = false;
      void loadProjectNameIfNeeded();
    }
    void refreshUnreadCount();
  },
  { immediate: true },
);

watch(hasPageTabs, (val) => {
  if (!val) {
    shellStore.setDashboardExpandedAiComposer(true);
  }
  // 有页签时不强制退出全屏，由用户通过「<」「>」自行切换
}, { immediate: true });

watch(
  () => route.name,
  (n) => {
    if (n === 'ProjectCreate' && shellStore.showProjectAutofillIntro) {
      void nextTick(() => {
        window.setTimeout(() => shellStore.endProjectAutofillIntro(), 520);
      });
    }
  },
);

async function refreshUnreadCount(force = false) {
  const now = Date.now();
  if (!force && now - lastUnreadFetchAt < 1500) return;
  lastUnreadFetchAt = now;
  try {
    const res = await getMyUnreadCount();
    unreadCount.value = (res as any)?.count ?? 0;
  } catch {
    // ignore
  }
}

function tabDotState(tab: ShellPageTab): 'idle' | 'running' | 'filled' {
  if (shellStore.hasTabRunningTasks(tab.key)) return 'running';
  if (shellStore.hasTabCompletion(tab.key)) return 'filled';
  return 'idle';
}

async function handleTabClick(tab: ShellPageTab) {
  if (tab.key === shellStore.activePageTabKey) {
    shellStore.clearTabCompletion(tab.key);
    return;
  }
  try {
    const failure = await router.push(tab.to);
    if (failure) return;
    // 路由监听会在真正进入目标页后激活页签，避免守卫拦截时留下“已切换”的假状态。
    shellStore.clearTabCompletion(tab.key);
  } catch (error) {
    console.warn('页面页签跳转失败', error);
  }
}

function handleTabDragStart(tab: ShellPageTab, event: DragEvent) {
  draggingTabKey.value = tab.key;
  lastLiveReorderKey = '';
  if (event.dataTransfer) {
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', tab.key);
  }
  const el = event.currentTarget as HTMLElement | null;
  if (el && event.dataTransfer?.setDragImage) {
    event.dataTransfer.setDragImage(el, Math.min(24, el.offsetWidth / 2), Math.min(16, el.offsetHeight / 2));
  }
}

function handleTabDragOver(tab: ShellPageTab, event: DragEvent) {
  event.preventDefault();
  if (event.dataTransfer) event.dataTransfer.dropEffect = 'move';
  const fromKey = draggingTabKey.value;
  if (!fromKey || fromKey === tab.key) return;
  const currentTarget = event.currentTarget as HTMLElement | null;
  if (!currentTarget) return;
  const rect = currentTarget.getBoundingClientRect();
  const before = event.clientX < rect.left + rect.width / 2;
  const tabs = pageTabs.value;
  const fromIndex = tabs.findIndex((item) => item.key === fromKey);
  const overIndex = tabs.findIndex((item) => item.key === tab.key);
  if (fromIndex < 0 || overIndex < 0) return;
  let insertAt = before ? overIndex : overIndex + 1;
  if (fromIndex < insertAt) insertAt -= 1;
  const dedupeKey = `${fromKey}->${insertAt}`;
  if (dedupeKey === lastLiveReorderKey) return;
  if (insertAt === fromIndex) {
    lastLiveReorderKey = dedupeKey;
    return;
  }
  lastLiveReorderKey = dedupeKey;
  shellStore.movePageTabToIndex(fromKey, insertAt);
}

function handleTabDragEnd() {
  draggingTabKey.value = '';
  lastLiveReorderKey = '';
}

function tabIsProjectCreate(tab: ShellPageTab): boolean {
  const to = tab.to as { name?: string } | string;
  return typeof to === 'object' && to != null && to.name === 'ProjectCreate';
}

function draftKeyFromProjectCreateTab(tab: ShellPageTab): string {
  const to = tab.to as { query?: Record<string, unknown> } | string;
  if (typeof to !== 'object' || to == null) return '';
  const q = to.query?.draft;
  return Array.isArray(q) ? String(q[0] ?? '') : String(q ?? '');
}

async function handleTabClose(tab: ShellPageTab) {
  if (tabIsProjectCreate(tab)) {
    const dk = draftKeyFromProjectCreateTab(tab);
    const leave = await confirmProjectCreateDraftLeave({
      draftKey: dk,
      persistDraftFull: (key) => projectCreateDraftStore.persistDraftFull(key),
      removeDraftLocal: (key) => projectCreateDraftStore.removeDraftLocal(key),
      getDraftSnapshot: (key) =>
        projectCreateDraftStore.getWorking(key) ?? projectCreateDraftStore.loadPersistedDraft(key),
    });
    if (!leave) return;
  }
  const wasActive = shellStore.activePageTabKey === tab.key;
  shellStore.closePageTab(tab.key);
  if (!wasActive) return;
  const activeTab = shellStore.pageTabs.find((item) => item.key === shellStore.activePageTabKey);
  if (activeTab) {
    router.push(activeTab.to);
  } else {
    preserveEmptyDashboardTabs = true;
    if (router.currentRoute.value.name !== 'Dashboard') {
      await router.replace({ name: 'Dashboard' });
    }
  }
  shellStore.setDashboardExpandedAiComposer(!shellStore.hasPageTabs());
}

async function handleUserCommand(command: string) {
  if (command === 'profile') {
    router.push('/user/profile');
  } else if (command === 'logout') {
    await authStore.logout();
    await router.replace({ path: '/login' });
    authStore.endLoggingOut();
  }
}

const canOpenOrgAdmin = computed(
  () =>
    permissionStore.hasRoles(['SUPER_ADMIN', 'ADMIN']) &&
    (checkMatrixPermission(OrgAdminMatrix.orgTreeView) ||
      checkMatrixPermission(OrgAdminMatrix.adminView) ||
      checkMatrixPermission(OrgAdminMatrix.roleUsersView) ||
      checkMatrixPermission(OrgAdminMatrix.roleCreate)),
);

function goOrgAdmin() {
  router.push('/admin/org');
}

function goHelp() {
  router.push('/help');
}

</script>

<style scoped>
.tab-move-enter-active,
.tab-move-leave-active,
.tab-move-move {
  transition:
    transform 0.24s cubic-bezier(0.22, 1, 0.36, 1),
    opacity 0.22s ease,
    width 0.24s cubic-bezier(0.22, 1, 0.36, 1),
    margin 0.24s cubic-bezier(0.22, 1, 0.36, 1),
    flex-basis 0.24s cubic-bezier(0.22, 1, 0.36, 1);
}

.tab-move-enter-from,
.tab-move-leave-to {
  opacity: 0;
  transform: translateY(-8px) scale(0.96);
}

.tab-move-leave-active {
  position: absolute;
}

.app-shell {
  display: flex;
  min-height: 100vh;
  width: 100%;
  background: #f0f2f5;
}

.app-shell-body {
  flex: 1;
  min-width: 0;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  margin-left: v-bind(sidebarRailWidth + 'px');
  width: calc(100% - v-bind(sidebarRailWidth + 'px'));
  box-sizing: border-box;
  background: #f0f2f5;
}

.app-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  padding: 0 16px 0 0;
  background: #fff;
  border-bottom: none;
  position: fixed;
  top: 0;
  left: v-bind(sidebarRailWidth + 'px');
  right: 0;
  z-index: 1000;
}

.header-left {
  display: flex;
  align-items: flex-end;
  min-width: 0;
  flex: 1;
  align-self: stretch;
}

.page-tabs {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  overflow-x: auto;
  overflow-y: hidden;
  min-width: 0;
  flex: 1;
  max-width: 100%;
  height: 100%;
  margin-right: 12px;
  padding: 10px 0 0;
  scrollbar-width: thin;
  scrollbar-color: rgba(31, 35, 41, 0.35) transparent;
  overscroll-behavior-x: contain;
}

.page-tabs::-webkit-scrollbar {
  height: 6px;
}

.page-tabs::-webkit-scrollbar-thumb {
  background: rgba(31, 35, 41, 0.28);
  border-radius: 3px;
}

.page-tabs::-webkit-scrollbar-track {
  background: transparent;
}

.page-tab {
  position: relative;
  display: inline-flex;
  align-items: center;
  height: 32px;
  padding: 0 10px 0 12px;
  color: #4e5969;
  cursor: pointer;
  white-space: nowrap;
  border: 1px solid #e5e6eb;
  border-bottom: none;
  border-radius: 6px 6px 0 0;
  background: #fff;
  box-shadow: none;
  transition:
    opacity 0.15s ease,
    background-color 0.15s ease,
    border-color 0.15s ease,
    color 0.15s ease;
  outline: none;
  margin-bottom: 0;
}

.page-tab:focus-visible {
  border-color: #0052d9;
  box-shadow: 0 0 0 2px rgba(0, 82, 217, 0.16);
}

.page-tab.active {
  background: #f2f3f5;
  color: #0052d9;
  border-color: #e5e6eb;
  border-bottom: none;
  box-shadow: none;
  z-index: 2;
}

.page-tab.dragging {
  opacity: 0.45;
}

.page-tab:hover {
  border-color: #c9cdd4;
  border-bottom: none;
  color: #1d2129;
  background: #f7f8fa;
}

.page-tab.active:hover {
  border-color: #e5e6eb;
  border-bottom: none;
  background: #f2f3f5;
  color: #0052d9;
}

.page-tab-label {
  display: inline-block;
  max-width: 160px;
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: middle;
  font-size: 13px;
  font-weight: 400;
  line-height: 1;
  font-family: "PingFang SC", "Hiragino Sans GB", "Microsoft YaHei", sans-serif;
}

.page-tab-dot {
  display: inline-block;
  width: 6px;
  height: 6px;
  margin-right: 6px;
  border-radius: 50%;
  border: 1.5px solid currentColor;
  opacity: 0.4;
  flex-shrink: 0;
  vertical-align: middle;
  box-sizing: border-box;
}

.page-tab-dot--filled {
  background: #0052d9;
  border-color: #0052d9;
  opacity: 1;
  animation: none;
}

.page-tab-dot--running {
  border: 1.5px solid rgba(128, 138, 152, 0.28);
  border-top-color: #0052d9;
  border-right-color: #0052d9;
  background: transparent;
  opacity: 1;
  animation: page-tab-dot-spin 0.85s linear infinite;
}

@keyframes page-tab-dot-spin {
  to {
    transform: rotate(360deg);
  }
}

.page-tab.active .page-tab-dot {
  border-color: #0052d9;
}

.page-tab.active .page-tab-dot--filled {
  background: #0052d9;
  border-color: #0052d9;
}

.page-tab.active .page-tab-dot--running {
  border-color: rgba(0, 82, 217, 0.25);
  border-top-color: #0052d9;
  border-right-color: #0052d9;
}

.page-tab-close {
  margin-left: 6px;
  width: 16px;
  height: 16px;
  border: 0;
  background: transparent;
  color: inherit;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  cursor: pointer;
  opacity: 0.55;
  transition: opacity 0.15s ease, background-color 0.15s ease;
}

.page-tab:hover .page-tab-close,
.page-tab.active .page-tab-close,
.page-tab:focus-within .page-tab-close,
.page-tab.dragging .page-tab-close {
  opacity: 0.85;
}

.page-tab[data-key='Dashboard'] .page-tab-close {
  display: inline-flex;
  opacity: 0.55;
}

.page-tab-close:hover {
  background: rgba(0, 0, 0, 0.06);
  opacity: 1;
}

.header-right {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  align-self: center;
  height: 56px;
}

.header-tools {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-right: 16px;
}

.header-icon-btn {
  min-width: 28px;
  height: 28px;
  padding: 0 8px;
  border-radius: 50%;
  border: 1px solid var(--el-border-color-lighter);
  background: transparent;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  color: #4e5969;
}

.header-icon-btn:hover {
  background: var(--el-fill-color-light);
}

.header-text-btn {
  min-width: auto;
  padding: 0 10px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  letter-spacing: 0.02em;
  color: #1d2129;
  border-color: #c9cdd4;
  background: #fff;
}

.header-text-btn:hover {
  background: #f2f3f5;
  border-color: #86909c;
  color: #000;
}

.header-smoke-btn {
  height: 28px;
  padding: 0 12px;
  border-radius: 999px;
  border: 1px solid #f56c6c;
  background: linear-gradient(135deg, #ff6b6b 0%, #f56c6c 55%, #e6a23c 100%);
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.02em;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(245, 108, 108, 0.35);
  white-space: nowrap;
}

.header-smoke-btn:hover {
  filter: brightness(1.05);
}

.header-smoke-btn.is-running {
  animation: smoke-pulse 1.2s ease-in-out infinite;
}

.header-smoke-btn.is-paused {
  background: linear-gradient(135deg, #e6a23c 0%, #f0c78a 100%);
  border-color: #e6a23c;
  box-shadow: 0 2px 8px rgba(230, 162, 60, 0.35);
}

@keyframes smoke-pulse {
  0%,
  100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.04);
  }
}

.user-trigger {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
}

.user-trigger:hover {
  background: var(--el-fill-color-light);
}

.header-avatar {
  flex-shrink: 0;
}

.user-name {
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 14px;
}

.app-main {
  flex: 1;
  /* 与项目管理页一致：顶栏下约 8px 灰缝 + 左右/底 10px */
  padding: 64px 10px 10px;
  overflow: auto;
  background: #f0f2f5;
}

.app-main.app-main--project-create {
  padding: 64px 10px 10px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.app-main.app-main--project-dashboard {
  padding: 64px 10px 10px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.app-main.app-main--ai-smart-review {
  padding: 64px 10px 10px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-height: 0;
  height: 100vh;
  box-sizing: border-box;
}

.app-main.app-main--ai-smart-review > :deep(.ai-smart-review-page) {
  flex: 1 1 0;
  min-height: 0;
  height: auto;
  max-height: none;
  background: #fff;
  border-radius: 8px;
  border: 1px solid #e5e6eb;
  overflow: hidden;
}

.app-main.app-main--dashboard {
  /* 顶栏 56px + 约 8px 灰缝（页签底已贴顶栏下沿，无白条空隙） */
  padding: 64px 10px 10px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.app-main.app-main--org-admin {
  /* 顶栏 fixed：内容区铺满视口，禁止整页滚动，由组织架构内部列表滚动 */
  padding: 56px 0 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-height: 0;
  height: 100vh;
  box-sizing: border-box;
}

.app-main.app-main--org-admin > :deep(.org-admin-page) {
  flex: 1 1 0;
  min-height: 0;
  height: auto;
}

.app-main.app-main--smart-document {
  /* 与项目管理灰缝一致；禁止整页滚动，由三栏各自滚动 */
  padding: 64px 10px 10px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-height: 0;
  height: 100vh;
  box-sizing: border-box;
}

.app-main.app-main--smart-document > :deep(.smart-document-workbench) {
  flex: 1 1 0;
  min-height: 0;
  height: auto;
}

.app-main.app-main--dashboard > :deep(.dashboard-page) {
  flex: 1 1 0;
  min-height: 0;
  height: auto;
  background: #fff;
  border-radius: 8px;
  border: 1px solid #e5e6eb;
  box-shadow: none;
  overflow: hidden;
}

.app-main.app-main--ai-tools,
.app-main.app-main--ai-calculator {
  padding: 64px 10px 10px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.app-main.app-main--ai-tools > :deep(.ai-tools-page) {
  flex: 1 1 0;
  min-height: 0;
  height: auto;
}

.app-main.app-main--ai-calculator > :deep(.ai-calculator-page),
.app-main.app-main--ai-calculator > :deep(.calculator-detail-root) {
  flex: 1 1 0;
  min-height: 0;
  height: auto;
}

.app-main.app-main--ai-requirement-ledger,
.app-main.app-main--resources {
  padding: 64px 10px 10px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.app-main.app-main--ai-requirement-ledger > :deep(.ai-requirement-ledger-page),
.app-main.app-main--resources > :deep(.component-library-page) {
  flex: 1 1 0;
  min-height: 0;
  height: auto;
  background: #fff;
  border-radius: 8px;
  border: 1px solid #e5e6eb;
  overflow: auto;
  box-sizing: border-box;
}

.app-main.app-main--knowledge-space {
  padding: 64px 10px 10px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.app-main.app-main--knowledge-space > :deep(.ks-page) {
  flex: 1 1 0;
  min-height: 0;
  height: auto;
  max-height: none;
  background: #fff;
  border-radius: 8px;
  border: 1px solid #e5e6eb;
  overflow: hidden;
}

.app-main.app-main--ai-3d-viewer {
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.app-main.app-main--ai-3d-viewer > :deep(.xindi-3d-page) {
  flex: 1 1 0;
  min-height: 0;
  height: auto;
}

.app-main.has-ai-panel {
  padding-right: var(--ai-panel-reserve, 380px);
}

.app-main.no-header {
  padding-top: 20px;
}

@media (max-width: 992px) {
  .app-main.has-ai-panel {
    padding-right: 20px;
  }

  .app-shell-body {
    margin-left: v-bind(sidebarRailWidth + 'px');
    width: calc(100% - v-bind(sidebarRailWidth + 'px'));
  }
}

.user-roles-header {
  padding: 8px 12px 12px;
  border-bottom: 1px solid var(--el-border-color-lighter);
  margin-bottom: 4px;
}

.roles-label {
  font-size: 12px;
  color: var(--el-text-color-secondary);
  margin-right: 8px;
}

.header-role-tag {
  margin-right: 6px;
  margin-bottom: 4px;
}

.project-autofill-intro-overlay {
  position: fixed;
  inset: 0;
  z-index: 4000;
  display: flex;
  align-items: center;
  justify-content: center;
  background: radial-gradient(ellipse at center, rgba(15, 23, 42, 0.55), rgba(15, 23, 42, 0.82));
  backdrop-filter: blur(6px);
}

.project-autofill-intro-inner {
  text-align: center;
  padding: 32px 40px;
  border-radius: 16px;
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.98), rgba(244, 248, 255, 0.96));
  box-shadow:
    0 0 0 1px rgba(0, 82, 217, 0.12),
    0 24px 48px rgba(15, 23, 42, 0.2);
  animation: project-autofill-intro-pop 0.55s cubic-bezier(0.22, 1, 0.36, 1) both;
}

.project-autofill-intro-ring {
  width: 52px;
  height: 52px;
  margin: 0 auto 16px;
  border-radius: 50%;
  border: 3px solid rgba(0, 82, 217, 0.2);
  border-top-color: #0052d9;
  animation: project-autofill-intro-spin 0.95s linear infinite;
}

.project-autofill-intro-title {
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: #1f2329;
  letter-spacing: 0.02em;
  animation: project-autofill-intro-shimmer 2.2s ease-in-out infinite;
}

.project-autofill-intro-sub {
  margin: 10px 0 0;
  font-size: 13px;
  color: #646a73;
}

.project-autofill-intro-fade-enter-active,
.project-autofill-intro-fade-leave-active {
  transition: opacity 0.35s ease;
}

.project-autofill-intro-fade-enter-from,
.project-autofill-intro-fade-leave-to {
  opacity: 0;
}

@keyframes project-autofill-intro-spin {
  to {
    transform: rotate(360deg);
  }
}

@keyframes project-autofill-intro-pop {
  from {
    opacity: 0;
    transform: scale(0.92) translateY(12px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

@keyframes project-autofill-intro-shimmer {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.82;
    color: #0052d9;
  }
}

</style>

<style>
/* 顶栏下拉需高于全屏 AI 对话层（z-index: 4500） */
.header-user-dropdown-popper.el-popper {
  z-index: 4600 !important;
}
</style>

<style>
/* 冒烟测试：全局点击高亮（非 scoped，作用于被驱动的页面元素） */
.smoke-drive-pulse {
  outline: 3px solid #f56c6c !important;
  outline-offset: 2px;
  box-shadow: 0 0 0 6px rgba(245, 108, 108, 0.35) !important;
  animation: smoke-drive-pulse-kf 0.45s ease-out;
  position: relative;
  z-index: 5;
}

@keyframes smoke-drive-pulse-kf {
  0% {
    transform: scale(1);
  }
  40% {
    transform: scale(1.04);
  }
  100% {
    transform: scale(1);
  }
}
</style>
